import { useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* TOAST NOTIFICATION */
import { toast } from "react-toast";

/* USER DEFINED COMPONENTS */
import BlogForm from "./blog-form";

/* API IMPORT */
import { addBlog } from "../../../service/api";

const validationSchema = yup.object().shape({
  title: yup.string().required("Article title is required"),
  summary: yup.string().required("Short summary is required"),
  content: yup
    .string()
    .test(
      "not-empty",
      "Content is required",
      (value) => value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    )
    .required("Content is required"),
});

const AddBlog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      title: "",
      slug: "",
      category: "Menopause",
      categoryBadge: "Hormone Health",
      summary: "",
      content: "",
      readTime: "5 min read",
      author: "WellCare Clinical Team",
      sortOrder: 0,
      seoTitle: "",
      seoDescription: "",
      isActive: true,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      const body = {
        title: values.title,
        slug: values.slug,
        category: values.category,
        categoryBadge: values.categoryBadge,
        summary: values.summary,
        content: values.content,
        readTime: values.readTime,
        author: values.author,
        seoTitle: values.seoTitle,
        seoDescription: values.seoDescription,
        isActive: values.isActive,
      };
      if (values.sortOrder !== "" && values.sortOrder !== null) {
        body.sortOrder = Number(values.sortOrder);
      }

      setLoading(true);
      addBlog(body)
        .then((res) => {
          if (res?.status) {
            toast.success(res?.message || "Health blog article added successfully");
            formik.resetForm();
            navigate("/blogs");
          } else {
            toast.error(res?.message || "Failed to add blog article");
          }
        })
        .catch((error) => {
          toast.error(
            error?.response?.data?.message || "Failed to add the health blog article"
          );
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <section className="p-5 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Add Health Blog | WellCare Pharmacy Admin</title>
      </Helmet>

      <form onSubmit={formik.handleSubmit}>
        <div className="container-full-width">
          <div className="block xl:flex lg:flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-ibm-bold text-black tracking-tight">
                Add Health Blog
              </h1>
              <h2 className="text-base xl:text-lg lg:text-lg font-ibm-regular text-greyText tracking-tight">
                Create a new health blog article for the website.
              </h2>
            </div>
            <div className="flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
              <Link to="/blogs">
                <Button className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-5 py-3 rounded-md border border-authBorder text-sm font-ibm-semibold text-black normal-case transition-all duration-300 ease-in-out">
                  Back to Blogs
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={loading}
                className="w-full xl:w-auto lg:w-auto whitespace-nowrap shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-ibm-semibold text-white normal-case transition-all duration-300 ease-in-out"
              >
                {loading ? "Please wait..." : "Add Health Blog"}
              </Button>
            </div>
          </div>

          <div className="mt-6">
            <BlogForm formik={formik} />
          </div>
        </div>
      </form>
    </section>
  );
};

export default AddBlog;
